/**
 * Converts various date formats to ISO string
 * @param dateValue - Date string, Date object, or null/undefined
 * @param isEndDate - If true, sets time to end of day (23:59:59), otherwise start of day (00:00:00)
 * @returns ISO string representation of the date
 */
export const convertToISOString = (dateValue: string | Date | undefined | null, isEndDate = false): string => {
  if (!dateValue) {
    return new Date().toISOString()
  }

  // If it's already a Date object, convert to ISO
  if (dateValue instanceof Date) {
    return dateValue.toISOString()
  }

  // If it's already a string, try to parse it
  if (typeof dateValue === 'string') {
    const trimmed = dateValue.trim()

    // If it's already in ISO format with time component, return as is
    if (trimmed.includes('T') && (trimmed.includes('Z') || trimmed.includes('+'))) {
      return trimmed
    }

    // If the string doesn't contain 'T', it's a date-only string - append time component
    if (!trimmed.includes('T')) {
      // Check if it's just a date (YYYY-MM-DD or similar formats without time)
      const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/
      const dateOnlyPattern2 = /^\d{2}\/\d{2}\/\d{4}$/
      const dateOnlyPattern3 = /^\d{4}\/\d{2}\/\d{2}$/

      let parsedDate: Date | null = null

      if (dateOnlyPattern.test(trimmed)) {
        // YYYY-MM-DD format
        parsedDate = new Date(trimmed)
      } else if (dateOnlyPattern2.test(trimmed)) {
        // Could be MM/DD/YYYY or DD/MM/YYYY - need to detect
        const [first, second, year] = trimmed.split('/')
        const firstNum = parseInt(first, 10)
        const secondNum = parseInt(second, 10)

        // If first part is > 12, it must be DD/MM/YYYY
        // If second part is > 12, it must be MM/DD/YYYY
        // Otherwise, assume DD/MM/YYYY (common in international format)
        if (firstNum > 12) {
          // DD/MM/YYYY
          parsedDate = new Date(`${year}-${second}-${first}`)
        } else if (secondNum > 12) {
          // MM/DD/YYYY
          parsedDate = new Date(`${year}-${first}-${second}`)
        } else {
          // Ambiguous - default to DD/MM/YYYY
          parsedDate = new Date(`${year}-${second}-${first}`)
        }
      } else if (dateOnlyPattern3.test(trimmed)) {
        // YYYY/MM/DD format
        parsedDate = new Date(trimmed.replace(/\//g, '-'))
      } else {
        // Try to parse as a general date format
        parsedDate = new Date(trimmed)
      }

      // Check if date is valid
      if (parsedDate && !Number.isNaN(parsedDate.getTime())) {
        // Add default time: start of day (00:00:00) for start dates, end of day (23:59:59) for end dates
        if (isEndDate) {
          parsedDate.setHours(23, 59, 59, 999)
        } else {
          parsedDate.setHours(0, 0, 0, 0)
        }
        return parsedDate.toISOString()
      }

      // If parsing failed, try to extract just the date part and append time
      const dateMatch = trimmed.match(/^\d{4}-\d{2}-\d{2}/)
      if (dateMatch) {
        const datePart = dateMatch[0]
        const timePart = isEndDate ? 'T23:59:59.000Z' : 'T00:00:00.000Z'
        return `${datePart}${timePart}`
      }

      if (process.env.NODE_ENV === 'development') {
        console.warn(`Invalid date format: ${trimmed}, using current date`)
      }
      return new Date().toISOString()
    }

    // If it has 'T' but no timezone, try to parse and add timezone
    const date = new Date(trimmed)

    // Check if date is valid
    if (Number.isNaN(date.getTime())) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Invalid date format: ${trimmed}, using current date`)
      }
      return new Date().toISOString()
    }

    return date.toISOString()
  }

  // Fallback to current date
  return new Date().toISOString()
}
